import type { trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { DialogConfig } from '@svs/nova-foundation/nextjs/dialog';
import { accountBalanceMw } from '@svs/nova-payment-sport';
import { createElement } from 'react';

import { getFormattedCurrency } from '../../lib/entry.browser.js';

/**
 * Middleware to check the user's account balance before confirming a payment interaction, with dialog integration.
 *
 * @param showDialog The function to show a dialog.
 * @param paymentUrls Optional payment URLs for redirecting in payment middlewares.
 * @param trackEventFunction Optional function to track analytics events, used for tracking when users interact with the account balance dialog.
 * @returns Middleware function that checks account balance and shows a dialog if the balance is insufficient.
 */
export function accountBalance(
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>,
  paymentUrls?: Record<string, string | undefined>,
  trackEventFunction?: typeof trackEvent
): ReturnType<typeof accountBalanceMw> {
  return accountBalanceMw(
    (interactionContext) =>
      new Promise((resolve, reject) => {
        const { title, messages } = interactionContext ?? {};
        showDialog({
          title: title || 'Du har inte tillräckligt med pengar på ditt spelkonto',
          description:
            messages && messages.length > 0
              ? messages.map((message, index) =>
                  // eslint-disable-next-line react/no-array-index-key
                  createElement('p', { key: index, className: 'text-center lg:text-left' }, message || '')
                )
              : undefined,
          buttons: {
            primary: {
              label: 'Sätt in',
              action: () => {
                resolve(); // Continue with redirect to deposit page
              },
            },
            secondary: {
              label: 'Avbryt',
              action: () => {
                reject(new Error('Payment cancelled by customer')); // Cancel payment
              },
            },
          },
        }).catch(() => {
          // If the dialog is closed in another way (ESC, click outside, etc.)
          reject(new Error('Payment cancelled by customer'));
        });
      }),
    { paymentUrls, currencyFormatter: getFormattedCurrency, trackEventFunction }
  );
}
